import { Box, Button, Typography } from "@mui/material";
import { MainContainer } from "../../components/MainContainerComp";
import { ProgressBar } from "../../components/ProgressBar";
import { ITheme, theme } from "../../utils/theme";
import { Confirmation } from "./components/Confirmation";
import { DaysRequested } from "./components/DaysRequested";
import { LeaveDetails } from "./components/LeaveDetails";
import { PersonalInfo } from "./components/PersonalInfo";
import { IEmployeeInfo, IPaidMedicalLeaveRequestData } from "./PaidMedicalLeaveRequestData";
import { IDisclaimer, ProgressBarSteps } from "./PaidMedicalLeaveRequestModels";

interface IPaidMedicalLeaveRequestProps {
  data: IPaidMedicalLeaveRequestData;
  activeStep: string;
  onChangeField: <K extends keyof IEmployeeInfo>(
    label: K,
    value: IEmployeeInfo[K]
  ) => void;
  onSubmit: () => void;
  onUpdateEmail: () => void;
  error: Record<string, JSX.Element | string>;
  onBack: () => void;
  onNext: () => void;
  disclaimer: IDisclaimer,
  onShowDisclaimer: (dislaimer: IDisclaimer) => void;
  isNextDisabled: boolean;
  isSubmitting: boolean;
  onDateChange: (field: keyof IEmployeeInfo, value: string) => void;
  onDateBlur: (field: keyof IEmployeeInfo) => void;
  onFileChange: (files: FileList | []) => void;
  onRemoveFile: (index: number) => void;
  blurredFields: Partial<Record<keyof IEmployeeInfo, boolean>>
}

export const PaidMedicalLeaveRequest: React.FC<IPaidMedicalLeaveRequestProps> = ({
  data,
  activeStep,
  onChangeField,
  onSubmit,
  onUpdateEmail,
  error,
  onBack,
  onNext,
  disclaimer,
  onShowDisclaimer,
  isNextDisabled,
  isSubmitting,
  onDateChange,
  onDateBlur,
  onFileChange,
  onRemoveFile,
  blurredFields
}) => {
  const isTheLastStep = activeStep === ProgressBarSteps.Confirmation;
  const isTheFirstStep = activeStep === ProgressBarSteps.PersonalInfo;
  const isSubmitDisabled = isNextDisabled || (isTheLastStep && isSubmitting);
  
  return (
    <MainContainer hasGreyBg>
      <ProgressBar steps={Object.values(ProgressBarSteps)} activeStep={activeStep} />
      {activeStep === ProgressBarSteps.PersonalInfo && (
        <>
          {<Typography sx={{ fontWeight: 'bold', color: theme.palette.corporateBlue, pt: 2, pb: 3 }}>
            To submit your application online, please complete the fields marked with an asterisk
            (<Typography component={"span"} sx={{ color: (theme: ITheme) => theme.palette.red.A700, mx: 0.5 }}>*</Typography>).
          </Typography>}
          <PersonalInfo
            onChangeField={onChangeField}
            data={data}
            onUpdateEmail={onUpdateEmail}
            error={error}
          />
        </>
      )}
      {activeStep === ProgressBarSteps.LeaveDetails && <LeaveDetails
        blurredFields={blurredFields}
        onChangeField={onChangeField}
        data={data}
        onShowDisclaimer={onShowDisclaimer}
        disclaimer={disclaimer}
        error={error}
      />}
      {activeStep === ProgressBarSteps.DaysRequested && <DaysRequested
        onDateChange={onDateChange}
        onDateBlur={onDateBlur}
        onFileChange={onFileChange}
        onRemoveFile={onRemoveFile}
        data={data}
        onShowDisclaimer={onShowDisclaimer}
        disclaimer={disclaimer}
        error={error}
        blurredFields={blurredFields}
      />}
      {activeStep === ProgressBarSteps.Confirmation && <Confirmation
        onChangeField={onChangeField}
        data={data}
        error={error}
        blurredFields={blurredFields}
      />}
      <Box sx={{ display: 'flex', justifyContent: isTheFirstStep ? 'center' : 'space-between', mt: 2, px: 2 }}>
        {!isTheFirstStep && <Button onClick={onBack} variant="contained" disabled={isSubmitting}>Back</Button>}
        <Button 
          onClick={isTheLastStep ? onSubmit : onNext} 
          sx={{ width: isTheFirstStep ? '150px' : 'auto' }} 
          variant="contained" 
          disabled={isSubmitDisabled}
        >
          {isTheLastStep ? (isSubmitting ? 'Submitting...' : 'Submit') : 'Next'}
        </Button>
      </Box>
    </MainContainer>
  );
};
